// @spec GHG-STR-010
'use client';

/**
 * Dropdown selector for data quality tier with explanatory descriptions.
 * Implements the 4-tier quality system per GHG Protocol.
 */

import type { DataQuality } from '../types';

import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
interface DataQualitySelectProps {
  value: DataQuality;
  onChange: (value: DataQuality) => void;
  disabled?: boolean;
  allowedValues?: DataQuality[] | undefined;
}

interface DataQualityOption {
  value: DataQuality;
  label: string;
  description: string;
  score: number;
  colorClasses: string;
}

/**
 * Data quality tier options with descriptions and colors
 */
const DATA_QUALITY_OPTIONS: DataQualityOption[] = [
  {
    value: 'measured',
    label: 'Measured',
    description: 'Direct measurement from meters, invoices, or tracking systems',
    score: 5,
    colorClasses: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  },
  {
    value: 'calculated',
    label: 'Calculated',
    description: 'Derived from other measured data using standard methods',
    score: 4,
    colorClasses: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  },
  {
    value: 'estimated',
    label: 'Estimated',
    description: 'Based on reasonable estimates using activity data and factors',
    score: 3,
    colorClasses: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  },
  {
    value: 'spend_based',
    label: 'Spend-based',
    description: 'Estimated from financial spend data - lowest accuracy tier',
    score: 1,
    colorClasses: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
];

/**
 * Convert DATA_QUALITY_OPTIONS to Select-compatible options
 */
function toSelectOptions(allowedValues?: DataQuality[]) {
  const allowedSet = allowedValues ? new Set(allowedValues) : null;
  return DATA_QUALITY_OPTIONS.filter((opt) =>
    allowedSet ? allowedSet.has(opt.value) : true
  ).map((opt) => ({
    value: opt.value,
    label: `${opt.label} (Score: ${String(opt.score)})`,
  }));
}

/**
 * Get data quality option by value
 */
function getQualityOption(value: DataQuality): DataQualityOption {
  const found = DATA_QUALITY_OPTIONS.find((opt) => opt.value === value);
  // Fallback to 'measured' - array is guaranteed non-empty
  return (
    found ?? {
      value: 'measured' as const,
      label: 'Measured',
      description: 'Direct measurement from meters, invoices, or tracking systems',
      score: 5,
      colorClasses: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    }
  );
}

export function DataQualitySelect({
  value,
  onChange,
  disabled = false,
  allowedValues,
}: DataQualitySelectProps) {
  const { t } = useTranslation('ghg');
  const options = toSelectOptions(allowedValues);
  return (
    <Select
      options={options}
      value={value}
      onChange={(val) => {
        onChange(val as DataQuality);
      }}
      disabled={disabled}
      placeholder={t('placeholder.selectDataQuality')}
    />
  );
}

/**
 * Display-only badge for data quality (used in list views)
 */
export function DataQualityTierBadge({
  quality,
  showScore = false,
}: {
  quality: DataQuality;
  showScore?: boolean;
}) {
  const option = getQualityOption(quality);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className={cn('text-xs px-2 py-0.5', option.colorClasses)}>
            {option.label}
            {showScore && ` (${String(option.score)})`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{option.label} Data Quality</p>
          <p className="text-xs text-muted-foreground">{option.description}</p>
          <p className="mt-1 text-xs">Score: {String(option.score)} / 5</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
